import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { COMPLIANCE_BENEFITS } from "@/lib/hardware";
import { AlertTriangle, ArrowRight, Cctv } from "lucide-react";

export default function Compliance() {
  return (
    <section
      id="compliance"
      data-testid="compliance-section"
      className="relative grain bg-[#2B2B2B] text-white py-20 lg:py-28 overflow-hidden"
    >
      <div className="absolute inset-0 techgrid-dark opacity-60" />
      <div className="absolute -top-32 right-0 w-[520px] h-[520px] rounded-full bg-[#F5A623]/10 blur-3xl" />

      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
        {/* urgency ribbon */}
        <Reveal>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#F5A623]/40 bg-[#F5A623]/10 px-4 py-1.5 overline text-[#F5A623]">
            <AlertTriangle size={13} /> South Australia · 2027 regulation
          </div>
        </Reveal>

        <div className="mt-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* left: the mandate */}
          <div className="lg:col-span-6">
            <Reveal delay={0.05}>
              <div className="flex items-end gap-4">
                <span className="font-display font-extrabold leading-none tracking-tight text-[26vw] sm:text-[9rem] lg:text-[11rem] text-[#F5A623]">
                  2027
                </span>
              </div>
              <h2 className="mt-2 font-display font-extrabold tracking-tight text-3xl sm:text-4xl leading-[1.05]">
                From 2027, a dashcam won't be optional.
              </h2>
              <p className="mt-6 max-w-xl text-lg text-white/70 leading-relaxed">
                New South Australian regulations will require every driving instructor to run a dashcam
                that records the <span className="text-white font-medium">cabin</span> and the{" "}
                <span className="text-white font-medium">forward view simultaneously</span>. Non-compliant
                vehicles won't be permitted to teach.
              </p>
              <p className="mt-5 max-w-xl text-lg font-medium leading-relaxed text-white">
                Doing nothing isn't an option. DriveAssure Pro+ is the hassle-free way to get sorted —
                well before the deadline.
              </p>

              <a
                href="#proplus"
                data-testid="compliance-cta"
                className="group mt-9 inline-flex items-center gap-2 rounded-full bg-[#2BBFB3] px-7 py-4 font-semibold text-[#0f1211] hover:bg-[#1FA097] transition-colors"
              >
                See DriveAssure Pro+ plans
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </a>
            </Reveal>
          </div>

          {/* right: the easy path */}
          <div className="lg:col-span-6">
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur">
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#2BBFB3]/15 text-[#2BBFB3]">
                    <Cctv size={22} />
                  </span>
                  <p className="font-display font-bold text-xl">The hassle-free path to compliance</p>
                </div>
                <div className="mt-7 space-y-5">
                  {COMPLIANCE_BENEFITS.map((b, i) => (
                    <Reveal key={b.title} delay={0.15 + i * 0.08}>
                      <div className="flex gap-4">
                        <span className="grid place-items-center w-10 h-10 rounded-xl bg-white/5 text-[#F5A623] shrink-0">
                          <b.icon size={19} />
                        </span>
                        <div>
                          <p className="font-semibold text-white">{b.title}</p>
                          <p className="mt-1 text-sm text-white/60 leading-relaxed">{b.desc}</p>
                        </div>
                      </div>
                    </Reveal>
                  ))}
                </div>
                <p className="mt-7 pt-6 border-t border-white/10 text-xs text-white/40 leading-relaxed">
                  Installation carried out by qualified auto electricians at approved locations across South
                  Australia. Locations announced soon.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
